// Task 10 (WU-2.4) — supervisor-facing active-evictions panel. Sized as a
// mountable panel (not a page): mounted on OpsConsolePage (below the
// operator grid) and StaffMonitorPage (below the live roster).
//
// Self-contained — owns its own fetch (evictionsApi.list) and refetches
// after this viewer's own Clear action, the delegation-list pattern also
// used by QueueInstructionsListPage. Deliberately NOT WS-wired: spec §8 —
// supervisors are not subscribed to the operators' private eviction
// channels (queue_eviction / queue_eviction_cleared are per-user events),
// so this panel is poll-on-mount + refetch-on-action, not live.
//
// The list route is gated server-side on supervisor.queue_evict (the same
// cap canQueueEvict checks client-side), so the fetch is skipped entirely
// when the viewer lacks the cap rather than firing a fetch that's known to
// 403. Defense in depth: an actual 403 (e.g. stale client-side permissions)
// is treated the same as "nothing to show" — no error toast, render null.
//
// AdminEviction.jobTypeId is a bare id — the list route (unlike
// queue-instructions) doesn't resolve job-type names, so this renders the
// raw id in the scope label. Acceptable for v1 per the brief; a follow-up
// could thread useJobTypes through if a mount page already has it in scope
// (neither OpsConsolePage nor StaffMonitorPage currently do at this level).
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api-client";
import { getApiErrorCode, showApiError } from "@/lib/error-toast";
import { formatTimestamp } from "@/lib/formatters";
import { evictionsApi, type AdminEviction, type MyEviction } from "../api/client";
import { canQueueEvict } from "../lib/permissions";
import styles from "../queue-evictions.module.css";

const MODE_LABELS: Record<MyEviction["mode"], string> = {
  immediate: "Immediate",
  after_document: "After document",
  after_batch: "After batch",
};

function scopeLabel(stepType: string, jobTypeId: string | null): string {
  return jobTypeId === null ? `${stepType} — whole step` : `${stepType} / ${jobTypeId}`;
}

interface EvictionRowsProps {
  rows: AdminEviction[];
  showActions: boolean;
  onClear?: (id: string) => void;
}

function EvictionRows({ rows, showActions, onClear }: EvictionRowsProps) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Operator</th>
          <th>Scope</th>
          <th>Mode</th>
          <th>Reason</th>
          <th>By</th>
          <th>At</th>
          <th>Expires</th>
          {showActions && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{row.userName}</td>
            <td>{scopeLabel(row.stepType, row.jobTypeId)}</td>
            <td>{MODE_LABELS[row.mode]}</td>
            <td>{row.reason}</td>
            <td>{row.createdByName}</td>
            <td>{formatTimestamp(row.createdAt)}</td>
            <td>{formatTimestamp(row.expiresAt)}</td>
            {showActions && (
              <td>
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={() => onClear?.(row.id)}
                >
                  Clear
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function EvictionList() {
  const { user } = useAuth();
  const canManage = canQueueEvict(user);

  // WU-2.4 /simplify — a single source of truth (the raw list response);
  // active/cleared are derived views, not independently-set state, so they
  // can never disagree with each other or with the fetch that produced them.
  const [evictions, setEvictions] = useState<AdminEviction[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const active = evictions.filter((e) => e.clearedAt === null);
  const cleared = evictions.filter((e) => e.clearedAt !== null);

  const refetch = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const res = await evictionsApi.list();
      setEvictions(res.evictions);
      setForbidden(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        // Viewer lacks the cap server-side too — nothing to show, no toast.
        setForbidden(true);
      } else {
        showApiError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  async function handleClear(id: string) {
    if (!window.confirm("Clear this eviction? The operator can rejoin this lane immediately.")) {
      return;
    }
    try {
      await evictionsApi.clear(id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && getApiErrorCode(e) === "already_cleared") {
        showApiError(e, { fallback: "Already cleared — refreshing the list." });
      } else {
        showApiError(e);
        return;
      }
    }
    await refetch();
  }

  // No cap → the list route would 403 anyway; render nothing rather than
  // firing a doomed fetch. Same for a defensive 403 caught above.
  if (!canManage || forbidden) return null;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>Active Evictions</h2>
      {loading ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : active.length === 0 ? (
        <p className={styles.emptyState}>No active evictions.</p>
      ) : (
        <EvictionRows rows={active} showActions onClear={(id) => void handleClear(id)} />
      )}

      {cleared.length > 0 && (
        <details className={styles.clearedDetails}>
          <summary>Cleared (last 30 days) ({cleared.length})</summary>
          <EvictionRows rows={cleared} showActions={false} />
        </details>
      )}
    </div>
  );
}

export default EvictionList;
